/**
 * CRUD data layer for the rec_weight_sets table.
 * Used by the RecWeightsSettings admin page.
 * Weight sets contain all tunable recommendation parameters (Issue #653).
 */
import type { RecWeightSet } from '@musedrops/shared';
import { fromDbKeys } from '@musedrops/shared';
import { supabase } from '../services/supabase';

/**
 * Manual camelCase→snake_case mapping for rec_weight_sets.
 * We can't use toDb() because snakeCase('maxPerShowTop10') produces
 * 'max_per_show_top_10' (underscore before digits), but the DB column is
 * 'max_per_show_top10'.
 */
const FIELD_MAP: Record<string, string> = {
  id: 'id',
  alphaShort: 'alpha_short',
  alphaLong: 'alpha_long',
  lambda: 'lambda',
  cooldownDays: 'cooldown_days',
  explorationRate: 'exploration_rate',
  followBoost: 'follow_boost',
  freshnessBoostMax: 'freshness_boost_max',
  freshnessDecayDays: 'freshness_decay_days',
  repeatPenaltyMax: 'repeat_penalty_max',
  repeatDecayDays: 'repeat_decay_days',
  maxPerShowTop10: 'max_per_show_top10',
  candidatePoolSize: 'candidate_pool_size',
  shortTermExpiryDays: 'short_term_expiry_days',
  cooldownMinPositionSec: 'cooldown_min_position_sec',
  cooldownMaxTimesListened: 'cooldown_max_times_listened',
  fullListenRatio: 'full_listen_ratio',
  inactivityTimeoutMin: 'inactivity_timeout_min',
  sameClusterGap: 'same_cluster_gap',
  sameShowGap: 'same_show_gap',
  isDefault: 'is_default',
  eventWeights: 'event_weights',
};

function toDb(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    const dbKey = FIELD_MAP[key];
    if (dbKey) result[dbKey] = value;
  }
  return result;
}

export async function listRecWeightSets(): Promise<RecWeightSet[]> {
  const { data, error } = await supabase
    .from('rec_weight_sets')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch weight sets: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<RecWeightSet>(row as Record<string, unknown>)
  );
}

export async function getRecWeightSet(id: string): Promise<RecWeightSet> {
  const { data, error } = await supabase
    .from('rec_weight_sets')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !data) {
    throw new Error(`Weight set not found: ${id}`);
  }

  return fromDbKeys<RecWeightSet>(data as Record<string, unknown>);
}

export async function createRecWeightSet(
  weightSet: Partial<RecWeightSet> & { id: string }
): Promise<RecWeightSet> {
  const { data, error } = await supabase
    .from('rec_weight_sets')
    .insert(toDb(weightSet))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create weight set: ${error.message}`);
  }

  return fromDbKeys<RecWeightSet>(data as Record<string, unknown>);
}

export async function updateRecWeightSet(
  id: string,
  updates: Partial<RecWeightSet>
): Promise<RecWeightSet> {
  const { data, error } = await supabase
    .from('rec_weight_sets')
    .update(toDb(updates))
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update weight set: ${error.message}`);
  }

  return fromDbKeys<RecWeightSet>(data as Record<string, unknown>);
}

export async function setDefaultWeightSet(id: string): Promise<void> {
  // Clear existing default first, then set the new one.
  // Order: clear old → set new. If step 2 fails, no default exists,
  // but the pipeline falls back gracefully (uses first row).
  // We clear first because the unique partial index on is_default=true
  // would reject setting a second row to true.
  const { error: clearErr } = await supabase
    .from('rec_weight_sets')
    .update({ is_default: false })
    .neq('id', id);

  if (clearErr) {
    throw new Error(`Failed to clear default: ${clearErr.message}`);
  }

  const { error: setErr } = await supabase
    .from('rec_weight_sets')
    .update({ is_default: true })
    .eq('id', id);

  if (setErr) {
    throw new Error(`Failed to set default: ${setErr.message}`);
  }
}

export async function deleteRecWeightSet(id: string): Promise<void> {
  const { error } = await supabase
    .from('rec_weight_sets')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Failed to delete weight set: ${error.message}`);
  }
}
